import { FileObject } from "./shared/file";

export interface BaseSupportCase {
  id: string;
  customerId?: string;
  customerName: string;
  customerEmail: string;
  type: "service" | "complaint";
  category: string;
  status: "not_started" | "in_progress" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  created: string;
  lastUpdated: string;
  regNr?: string;
  caseRef?: string;
  vehicleRegNr?: string;
  hasUnread?: boolean;
}

export interface SupportCaseDetails extends BaseSupportCase {
  description: string;
  files?: FileObject[];
  comments?: SupportComment[];
  messages?: SupportMessage[];
  historyEvents?: HistoryEvent[];
  damageLocation?: string;
  damageType?: string;
  engineComponent?: string;
  interiorSection?: string;
  wheelType?: string;
}

export interface HistoryEvent {
  id: string;
  supportCaseId: string;
  type: "status_change" | "priority_change" | "comment_added" | "file_uploaded" | "file_deleted" | "case_created" | "case_updated";
  action: string;
  timestamp: string;
  userId: string;
  userName: string;
  details?: {
    oldValue?: string;
    newValue?: string;
    fileName?: string;
    commentId?: string;
  };
}


export interface SupportComment {
  id: string;
  supportCaseId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
  isInternal: boolean;
}

export interface SupportMessage {
  id: string;
  supportCaseId: string;
  content: string;
  senderType: "customer" | "admin";
  senderName: string;
  createdAt: string;
}

export interface CreateSupportCaseRequest {
  regNr?: string;
  type: "service" | "complaint";
  customerId: string;
  category: string;
  description: string;
  files?: File[];
  damageLocation?: string;
  damageType?: string;
  engineComponent?: string;
  interiorSection?: string;
  wheelType?: string;
}

export interface UpdateSupportCaseRequest {
  status?: "not_started" | "in_progress" | "resolved" | "closed";
  priority?: "low" | "medium" | "high" | "urgent";
  category?: string;
  description?: string;
  files?: FileObject[];
  deleteFile?: string;
}

export interface AddCommentRequest {
  supportCaseId: string;
  content: string;
  isInternal?: boolean;
  notifyColleagues?: string[]; // Array of colleague names to notify
}

export interface SupportCaseFilters {
  regNr?: string;
  id?: string;
  customer?: string;
  type?: "service" | "complaint";
  status?: "not_started" | "in_progress" | "resolved" | "closed";
  priority?: "low" | "medium" | "high" | "urgent";
}

// Service categories (support cases — not bug reports)
export const SERVICE_CATEGORIES = [
  "technical_issue",
  "buying_selling",
  "other",
] as const;

// Complaint categories
export const COMPLAINT_CATEGORIES = [
  "engine",
  "car_meter",
  "vehicle_body",
  "interior",
  "electronics",
  "transmission",
  "brakes",
  "suspension",
  "other"
] as const;

export type ServiceCategory = typeof SERVICE_CATEGORIES[number];
export type ComplaintCategory = typeof COMPLAINT_CATEGORIES[number];

// Status labels
export const STATUS_LABELS = {
  not_started: "Ej påbörjad",
  in_progress: "Pågående", 
  resolved: "Löst",
  closed: "Stängd"
} as const;

// Priority labels
export const PRIORITY_LABELS = {
  low: "Låg",
  medium: "Medium",
  high: "Hög", 
  urgent: "Akut"
} as const;

// Category labels
export const CATEGORY_LABELS = {
  // Service categories
  technical_issue: "Tekniskt problem",
  buying_selling: "Köpa & sälja",
  other: "Övrigt",
  
  // Complaint categories
  engine: "Motor",
  car_meter: "Vägmätare",
  vehicle_body: "Kaross",
  interior: "Inredning",
  electronics: "Elektronik",
  transmission: "Växellåda",
  brakes: "Bromsar",
  suspension: "Fjädring",

  // Backend categories (fallback labels)
  technical: "Tekniskt problem",
  billing: "Fakturering",
  auction: "Auktion",
  vehicle: "Fordon",
  account: "Konto",
} as const;

export interface AddSupportCaseDocumentRequest {
  supportCaseId: string;
  document: File;
}

export interface RemoveSupportCaseDocumentRequest {
  supportCaseId: string;
  documentId: string;
}